import { useState, useEffect, useRef } from "react";
import { Bell, X, Check, CheckCheck, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  contact: { color: "bg-blue-500", label: "Contatto" },
  quote: { color: "bg-amber-500", label: "Preventivo" },
  shipment: { color: "bg-emerald-500", label: "Spedizione" },
  pickup: { color: "bg-violet-500", label: "Ritiro" },
  cancel: { color: "bg-red-500", label: "Annullamento" },
  pod: { color: "bg-green-600", label: "POD" },
};

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
  return `${Math.floor(diff / 86400)}g fa`;
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem("mv_admin_token");

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/api/admin/notifications/${id}/read`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/admin/notifications/read-all`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchNotifications();
  };

  const deleteNotif = async (id) => {
    await fetch(`${API}/api/admin/notifications/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchNotifications();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative p-2 text-zinc-600 hover:text-[#09090B] transition-colors"
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#FF5200] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-2rem)] max-h-[480px] bg-white rounded-lg shadow-xl border border-zinc-200 z-50 flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <span className="font-bold text-sm text-[#09090B] uppercase tracking-wide">
              Notifiche {unreadCount > 0 && `(${unreadCount})`}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                data-testid="mark-all-read-btn"
                className="text-xs text-[#FF5200] hover:underline flex items-center gap-1"
              >
                <CheckCheck size={14} /> Segna tutte lette
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-zinc-400 text-sm">
                Nessuna notifica
              </div>
            ) : (
              notifications.map((n) => {
                const typeInfo = TYPE_ICONS[n.type] || { color: "bg-zinc-400", label: n.type };
                return (
                  <div
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors ${
                      !n.read ? "bg-orange-50/40" : ""
                    }`}
                  >
                    <div className={`${typeInfo.color} w-2 h-2 rounded-full mt-2 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${!n.read ? "font-semibold text-[#09090B]" : "text-zinc-600"}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-zinc-400 mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-zinc-300 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!n.read && (
                        <button
                          onClick={() => markRead(n.id)}
                          className="p-1 text-zinc-300 hover:text-[#FF5200] transition-colors"
                          title="Segna come letta"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotif(n.id)}
                        className="p-1 text-zinc-300 hover:text-red-500 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
